// src/App.js — bản chuẩn, an toàn cho web1122
import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import ScrollToTop from "./components/ScrollToTop";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { Toaster } from "react-hot-toast";

// Các trang hiện có của site
import Home from "./pages/Home";
import LoginPage from "./pages/LoginPage";
import ServiceDetail from "./pages/ServiceDetail";
import PostForm from "./pages/PostForm";
import GioiThieu from "./pages/GioiThieu";
import BeHocTrongLucLacPage from "./pages/BeHocTrongLucLacPage";

// Admin
import AdminLayout from "./pages/AdminLayout";
import AdminHome from "./pages/AdminHome";
import AdminUsers from "./pages/AdminUsers";
import AdminServices from "./pages/AdminServices";
import AdminMaintenance from "./pages/AdminMaintenance";
import AdminMaintenanceTools from "./pages/AdminMaintenanceTools";
import AdminPosts from "./pages/AdminPosts";
import AdminDrumLessons from "./pages/AdminDrumLessons";

// Auth & quyền
import { AuthProvider } from "./auth/useAuth";
import ProtectedRoute from "./auth/ProtectedRoute";
import { ROUTES } from "./auth/acl";

import RegisterGuidePage from "./pages/RegisterGuidePage";
import NotificationsPage from "./pages/NotificationsPage";

// Trang Quiz (lazy-load)
const Quiz = lazy(() => import("./pages/quiz/Quiz"));

function useRightsMap() {
  return React.useMemo(() => {
    const map = {};
    for (const r of ROUTES) map[r.path] = r.rights || [];
    return map;
  }, []);
}

function AppShell() {
  const rightsMap = useRightsMap();
  const location = useLocation();
  const isQuizRoute = location.pathname.startsWith("/quiz");

  return (
    <div className="min-h-[100svh] flex flex-col">
      <ScrollToTop />

      {/* Nhánh /quiz chạy độc lập, không bọc Auth, không Header/Footer */}
      {isQuizRoute ? (
        <main className="flex-1 overflow-auto">
          <Routes>
            <Route
              path="/quiz"
              element={
                <Suspense fallback={<div className="p-4">Đang tải trang trắc nghiệm…</div>}>
                  <Quiz />
                </Suspense>
              }
            />
            <Route path="*" element={<Navigate to="/quiz" replace />} />
          </Routes>
        </main>
      ) : (
        <AuthProvider>
          <Header />
          <Toaster position="top-center" toastOptions={{ duration: 1800 }} containerStyle={{ top: 70 }} />

          <main className="flex-1 overflow-auto pb-[calc(72px+env(safe-area-inset-bottom))]">
            <Routes>
              {/* Public */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/services/:id" element={<ServiceDetail />} />

              <Route
                path="/dang-tin"
                element={
                  <ProtectedRoute rights={rightsMap["/dang-tin"]} fallback="/login">
                    <PostForm />
                  </ProtectedRoute>
                }
              />

              <Route path="/gioi-thieu" element={<GioiThieu />} />
              <Route path="/be-hoc-trong-luc-lac" element={<BeHocTrongLucLacPage />} />

              {/* Đăng ký & hướng dẫn */}
              <Route path="/dang-ky-va-huong-dan" element={<RegisterGuidePage />} />
              <Route path="/dang-ky" element={<RegisterGuidePage />} />
              <Route path="/dang-ky-huong-dan" element={<RegisterGuidePage />} />

              {/* Thông báo (chỉ user có quyền mới vào được) */}
              <Route
                path="/thong-bao"
                element={
                  <ProtectedRoute rights={rightsMap["/thong-bao"]} fallback="/login">
                    <NotificationsPage />
                  </ProtectedRoute>
                }
              />

              {/* Admin */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute rights={rightsMap["/admin"]} fallback="/">
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route
                  index
                  element={
                    <ProtectedRoute rights={rightsMap["/admin"]} fallback="/">
                      <AdminHome />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="services"
                  element={
                    <ProtectedRoute rights={rightsMap["/admin/services"]} fallback="/">
                      <AdminServices />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="users"
                  element={
                    <ProtectedRoute rights={rightsMap["/admin/users"]} fallback="/">
                      <AdminUsers />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="maintenance"
                  element={
                    <ProtectedRoute rights={rightsMap["/admin/maintenance"]} fallback="/">
                      <AdminMaintenance />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="maintenance/tools"
                  element={
                    <ProtectedRoute rights={rightsMap["/admin/maintenance"]} fallback="/">
                      <AdminMaintenanceTools />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="posts"
                  element={
                    <ProtectedRoute rights={rightsMap["/admin/posts"]} fallback="/">
                      <AdminPosts />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="drum-lessons"
                  element={
                    <ProtectedRoute rights={rightsMap["/admin/drum-lessons"]} fallback="/">
                      <AdminDrumLessons />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>

          <Footer />
        </AuthProvider>
      )}
    </div>
  );
}

export default function App() {
  return <AppShell />;
}
